import React from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { FOCUS_AREA_OPTIONS, INVITE_STAGE_OPTIONS } from './constants';

const GuestGuideForm = ({
  formData,
  setFormData,
  selectedGuide,
  uploadingGuidePhotoId,
  handleGuidePhotoUpload,
  toggleFocusArea,
}) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Name *</label>
        <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="Full name" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Email</label>
        <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="email@example.com" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Phone <span className="text-gray-500">(not posted on site)</span></label>
        <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="(555) 123-4567" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Profile title</label>
        <Input value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="Guide Partner" />
        <p className="mt-1 text-xs text-gray-500">This is descriptive. It does not control email audiences or account permissions.</p>
      </div>
    </div>
    <fieldset className="rounded-lg border border-charcoal-lighter bg-charcoal/50 p-4">
      <legend className="px-1 text-sm text-gray-300">Account roles</legend>
      <p className="mb-3 text-xs text-gray-500">Choose the structured roles used for partner lists and email audiences.</p>
      <div className="flex flex-wrap gap-4">
        {[
          ['guide_partner', 'Guide Partner'],
          ['advisory_partner', 'Advisory Partner'],
        ].map(([value, label]) => (
          <label key={value} className="flex cursor-pointer items-center gap-2 text-sm text-gray-200">
            <input
              type="checkbox"
              checked={formData.account_roles.includes(value)}
              onChange={() => setFormData({
                ...formData,
                account_roles: formData.account_roles.includes(value)
                  ? formData.account_roles.filter((role) => role !== value)
                  : [...formData.account_roles, value],
              })}
              className="h-4 w-4 rounded border-charcoal-lighter accent-aqua"
            />
            {label}
          </label>
        ))}
      </div>
    </fieldset>
    <div>
      <label className="text-sm text-gray-400 mb-1 block">Photo URL</label>
      <div className="space-y-2">
        {formData.photo_url && (
          <div className="relative inline-block">
            <img src={formData.photo_url} alt="Preview" className="w-24 h-24 rounded-lg object-cover border border-charcoal-lighter" />
            <button type="button" onClick={() => setFormData({ ...formData, photo_url: '' })}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-sm hover:bg-red-600">×</button>
          </div>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          {selectedGuide?.guide_id && (
            <label className="flex items-center gap-2 px-4 py-2 bg-aqua/10 border border-aqua/30 rounded-lg cursor-pointer hover:bg-aqua/20 transition-colors">
              {uploadingGuidePhotoId === selectedGuide.guide_id ? (
                <Loader2 className="w-4 h-4 text-aqua animate-spin" />
              ) : (
                <Upload className="w-4 h-4 text-aqua" />
              )}
              <span className="text-aqua text-sm">Upload Headshot</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleGuidePhotoUpload(selectedGuide.guide_id, file);
                  }
                  e.target.value = '';
                }}
              />
            </label>
          )}
          {selectedGuide?.guide_id && <span className="text-gray-500 text-sm">or</span>}
          <Input
            value={formData.photo_url}
            onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1"
            placeholder="https://.../storage/v1/object/public/avatars/guides/..."
          />
        </div>
        {!selectedGuide?.guide_id && (
          <p className="text-xs text-gray-500">
            Save the guide first, then use Upload Headshot to store in Supabase Storage.
          </p>
        )}
      </div>
    </div>
    <div>
      <label className="text-sm text-gray-400 mb-1 block">Bio</label>
      <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
        className="bg-charcoal border-charcoal-lighter text-white min-h-[100px]" placeholder="Share their story and background..." />
    </div>
    <div>
      <label className="text-sm text-gray-400 mb-2 block">Focus Areas</label>
      <div className="flex flex-wrap gap-2">
        {FOCUS_AREA_OPTIONS.map(area => (
          <button key={area} type="button" onClick={() => toggleFocusArea(area)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              formData.focus_areas.includes(area)
                ? 'bg-aqua/20 text-aqua border-aqua/50'
                : 'bg-charcoal text-gray-400 border-charcoal-lighter hover:border-aqua/30'
            }`}>
            {area}
          </button>
        ))}
      </div>
    </div>
    <div>
      <label className="text-sm text-gray-400 mb-1 block">Expertise <span className="text-gray-500">(comma-separated)</span></label>
      <Input value={formData.expertise_text} onChange={(e) => setFormData({ ...formData, expertise_text: e.target.value })}
        className="bg-charcoal border-charcoal-lighter text-white" placeholder="Leadership, Communication, Public Speaking" />
    </div>
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Birth Year</label>
        <Input type="number" value={formData.birth_year} onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="1980" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Current City</label>
        <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white" placeholder="Dallas, TX" />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Status</label>
        <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="w-full px-3 py-2 rounded-md bg-charcoal border border-charcoal-lighter text-white">
          <option value="prospect">Prospect</option>
          <option value="invited">Invited</option>
          <option value="active">Active</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Invite Stage</label>
        <select value={formData.invite_stage} onChange={(e) => setFormData({ ...formData, invite_stage: e.target.value })}
          className="w-full px-3 py-2 rounded-md bg-charcoal border border-charcoal-lighter text-white">
          {INVITE_STAGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </div>
    <div>
      <label className="text-sm text-gray-400 mb-2 block">Social Profiles</label>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <span className="text-blue-400 w-6">in</span>
          <Input value={formData.linkedin} onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="LinkedIn URL" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-blue-500 w-6">f</span>
          <Input value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="Facebook URL" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-300 w-6">𝕏</span>
          <Input value={formData.twitter} onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="X (Twitter) URL" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-red-500 w-6">▶</span>
          <Input value={formData.youtube} onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="YouTube URL" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sky-400 w-6">☁</span>
          <Input value={formData.bluesky} onChange={(e) => setFormData({ ...formData, bluesky: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="BlueSky URL" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-aqua w-6">🌐</span>
          <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            className="bg-charcoal border-charcoal-lighter text-white flex-1" placeholder="Website URL" />
        </div>
      </div>
    </div>
    <div>
      <label className="text-sm text-gray-400 mb-1 block">Notes <span className="text-gray-500">(internal only)</span></label>
      <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        className="bg-charcoal border-charcoal-lighter text-white" placeholder="Internal notes about this guide..." />
    </div>
  </div>
);

export default GuestGuideForm;
